/**
 * block.bundle-picker — A-Møbler bundle (spisegruppe) picker.
 *
 * Pure helpers (exported, unit-tested) compute the Før total, availability, and
 * the /cart/add.js multi-line payload. The DOM wiring auto-inits in the browser
 * (see the bottom of this file). Money is handled in øre (integer minor units).
 * Modelled on Klubb's theme/assets/klubb-player-package.js.
 */

// First available option's variantId per slot (falls back to the first option).
export function defaultSelection(bundle) {
  const sel = {};
  (bundle.slots || []).forEach((slot) => {
    const opts = slot.options || [];
    const first = opts.find((o) => o.available) || opts[0];
    if (first) sel[slot.key] = first.variantId;
  });
  return sel;
}

// The option object for a chosen variant within a slot, or null.
export function optionFor(slot, variantId) {
  const opts = (slot && slot.options) || [];
  return opts.find((o) => o.variantId === variantId) || null;
}

// Før = Σ over slots (chosen option listPrice × slot.quantity), in øre.
export function computeFor(selection, bundle) {
  return (bundle.slots || []).reduce((sum, slot) => {
    const opt = optionFor(slot, selection[slot.key]);
    if (!opt) return sum;
    return sum + opt.listPrice * slot.quantity;
  }, 0);
}

// True only when every chosen option is availableForSale (handoff §8).
export function allAvailable(selection, bundle) {
  return (bundle.slots || []).every((slot) => {
    const opt = optionFor(slot, selection[slot.key]);
    return !!(opt && opt.available);
  });
}

// One /cart/add.js item per slot: quantity = slot.quantity, properties carry the
// bundle identifiers (never a price — Track 2 derives price from a trusted metafield).
export function buildAddItems(selection, bundle, bundleId) {
  return (bundle.slots || []).map((slot) => {
    const opt = optionFor(slot, selection[slot.key]);
    return {
      id: opt.variantId,
      quantity: slot.quantity,
      properties: {
        _bundle_id: bundleId,
        _bundle: bundle.handle,
        _bundle_name: bundle.name,
        _slot: slot.key,
        _bundle_unit: String(slot.quantity),
      },
    };
  });
}

// Format øre as Norwegian kroner: "4 998 kr", "599,50 kr". Deterministic (no Intl).
export function formatKr(ore) {
  const neg = ore < 0;
  const abs = Math.abs(Math.round(ore));
  const kr = Math.floor(abs / 100);
  const rem = abs % 100;
  const grouped = String(kr).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  const dec = rem === 0 ? '' : ',' + String(rem).padStart(2, '0');
  return (neg ? '-' : '') + grouped + dec + ' kr';
}

// Key of a combination by the parts' SKUs in slot order ("BN1810010330_BN1820030227").
// combination_images is keyed by SKU because SKUs survive a combined listings
// migration and variant IDs do not (theme#15). '' when a part has no SKU.
export function comboSkuKey(selection, bundle) {
  const skus = (bundle.slots || []).map((slot) => {
    const opt = optionFor(slot, selection[slot.key]);
    return opt && opt.sku ? opt.sku : '';
  });
  return skus.every(Boolean) ? skus.join('_') : '';
}

// Media IDs for a selection, in gallery order: the combination image (SKU key,
// then the legacy variant ID key), else bundle.image, then each selected
// part's own images. media = the data-bundle-media JSON of block.bundle-media.
export function selectionMedia(selection, bundle, media) {
  const m = media || {};
  const combos = m.combos || {};
  const idKey = (bundle.slots || []).map((s) => selection[s.key]).join('_');
  const skuKey = comboSkuKey(selection, bundle);
  const ids = [];
  const head = (skuKey && combos[skuKey]) || combos[idKey] || m.fallback;
  if (head) ids.push(head);
  (bundle.slots || []).forEach((slot) => {
    ((m.options || {})[String(selection[slot.key])] || []).forEach((id) => {
      if (ids.indexOf(id) === -1) ids.push(id);
    });
  });
  return ids;
}

// The hero image for the current selection. Prefers a combination-specific image
// (bundle.combinationImages keyed by SKUs, or "<slot0Variant>_<slot1Variant>_..."), else
// falls back to the selected option image of the first slot (the table). Returns ''.
export function heroImage(selection, bundle) {
  const slots = bundle.slots || [];
  if (slots.length === 0) return '';
  const combos = bundle.combinationImages || {};
  const skuKey = comboSkuKey(selection, bundle);
  if (skuKey && combos[skuKey]) return combos[skuKey];
  const comboKey = slots.map((s) => selection[s.key]).join('_');
  if (combos[comboKey]) return combos[comboKey];
  const primary = slots[0];
  const opt = optionFor(primary, selection[primary.key]);
  return (opt && (opt.hero || opt.swatch)) || '';
}

// Format øre with the shop's money format, the way assets/util.currency.js
// does for the rest of the theme, so the bundle page prints prices exactly as
// the Liquid `money` filter does elsewhere. Falls back to formatKr.
export function formatMoneyWith(ore, format) {
  if (!format) return formatKr(ore);
  const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
  if (!match) return formatKr(ore);
  const abs = Math.abs(Math.round(ore));
  const sign = ore < 0 ? '-' : '';
  const group = (n, sep) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, sep);
  const kr = Math.floor(abs / 100);
  const rem = String(abs % 100).padStart(2, '0');
  let value;
  switch (match[1]) {
    case 'amount_no_decimals': value = group(Math.round(abs / 100), ','); break;
    case 'amount_with_comma_separator': value = group(kr, '.') + ',' + rem; break;
    case 'amount_no_decimals_with_comma_separator': value = group(Math.round(abs / 100), '.'); break;
    case 'amount_no_decimals_with_space_separator': value = group(Math.round(abs / 100), ' '); break;
    case 'amount_with_space_separator': value = group(kr, ' ') + ',' + rem; break;
    default: value = group(kr, ',') + '.' + rem;
  }
  return format.replace(match[0], sign + value);
}

// Severity of one delivery status line, for "show the worst part" (theme#14,
// decided 13.10). Higher is worse. 0 means not resolved yet.
//   5 Tilvirkningsvare (no self-service purchase)
//   4 Utsolgt (blocked)
//   3 Bestillingsvare, plus the lead time in weeks as a fraction
//   2 collect only ("Kan hentes på ...")
//   1 in stock
export function statusSeverity(state, text) {
  const t = (text || '').trim();
  if (state && state.hidden) return 5;
  if (state && state.blocked) return 4;
  if (t.indexOf('Bestillingsvare') === 0) {
    const weeks = Number((t.match(/(\d+)\s*uke/) || [])[1] || 0);
    return 3 + Math.min(weeks, 999) / 1000;
  }
  if (t.indexOf('Kan hentes') === 0) return 2;
  return t ? 1 : 0;
}

// Locations where every part can be collected: each part's quantity at the
// location covers its units for `sets` bundles. parts: [{ node, units }],
// node is the Storefront ProductVariant node with storeAvailability.
export function commonPickupLocations(parts, candidates, sets) {
  const n = Math.max(1, sets || 1);
  return (candidates || []).filter((name) => (parts || []).every((part) => {
    const nodes = (part.node && part.node.storeAvailability && part.node.storeAvailability.nodes) || [];
    const hit = nodes.find((e) => e.location && e.location.name === name);
    return !!hit && (hit.quantityAvailable || 0) >= part.units * n;
  }));
}

// URL of one section of a page (the Section Rendering API), for swapping the
// bundle page to another bundle in its group without a reload.
export function sectionUrl(href, sectionId) {
  const [path, hash] = String(href).split('#');
  const joiner = path.indexOf('?') === -1 ? '?' : '&';
  return path + joiner + 'section_id=' + encodeURIComponent(sectionId) + (hash ? '#' + hash : '');
}

/* ============================================================
 * Browser wiring (auto-init). Guarded so the module stays importable in Vitest.
 * ============================================================ */

// Section HTML per URL, so a hover or focus can fetch a bundle of the group
// before the click, and a second visit costs nothing.
const sectionCache = new Map();

function fetchSection(href, sectionId) {
  const url = sectionUrl(href, sectionId);
  if (!sectionCache.has(url)) {
    const request = fetch(url, { credentials: 'same-origin' })
      .then((r) => { if (!r.ok) throw new Error('section ' + r.status); return r.text(); });
    request.catch(() => sectionCache.delete(url));
    sectionCache.set(url, request);
  }
  return sectionCache.get(url);
}

// Bundle group (bundle_gruppe, snippets/bundle.group-axes): a tile links to
// another bundle of the same model. Swap the page section to it, the way a
// combined listing swaps the product page: price, image, parts, delivery
// status and the add to cart all come from that bundle's own render. The
// cart contract is unchanged, each bundle adds its own lines.
export function swapToBundle(root, href) {
  const sectionId = root.getAttribute('data-section-id') || '';
  const wrapper = root.closest('.shopify-section') ||
    document.getElementById('shopify-section-' + sectionId);
  if (!sectionId || !wrapper || typeof DOMParser === 'undefined') {
    window.location.href = href;
    return;
  }
  root.setAttribute('aria-busy', 'true');
  fetchSection(href, sectionId)
    .then((html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.getElementById(wrapper.id) || doc.querySelector('.shopify-section');
      if (!next || !next.querySelector('[data-amobler-bundle-root]')) throw new Error('no bundle in section');
      wrapper.innerHTML = next.innerHTML;
      window.history.replaceState(window.history.state, '', href);
      // Galleries and other section scripts re-bind on this event, the same
      // event the theme editor sends after it re-renders a section.
      wrapper.dispatchEvent(new CustomEvent('shopify:section:load', {
        bubbles: true,
        detail: { sectionId },
      }));
      wrapper.querySelectorAll('[data-amobler-bundle-root]').forEach(initBundlePicker);
    })
    .catch(() => { window.location.href = href; });
}

function bindGroupAxes(root) {
  const sectionId = root.getAttribute('data-section-id') || '';
  root.querySelectorAll('[data-bundle-axis-value]').forEach((tile) => {
    const href = tile.getAttribute('href');
    if (!href) return;
    const warm = () => { if (sectionId) fetchSection(href, sectionId).catch(() => {}); };
    tile.addEventListener('pointerenter', warm, { once: true });
    tile.addEventListener('focus', warm, { once: true });
    tile.addEventListener('click', (event) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;
      event.preventDefault();
      if (tile.getAttribute('aria-current') === 'true') return;
      swapToBundle(root, href);
    });
  });
}

function initBundlePicker(root) {
  if (root.__bundlePickerReady) return;
  root.__bundlePickerReady = true;
  bindGroupAxes(root);
  const dataEl = root.querySelector('[data-amobler-bundle]');
  let bundle;
  try { bundle = JSON.parse(dataEl.textContent); }
  catch (e) { console.warn('[bundle-picker] invalid JSON blob — picker disabled', e); return; }
  if (!bundle || !Array.isArray(bundle.slots) || bundle.slots.length === 0) return;

  const prefix = root.getAttribute('data-bundle-prefix') || bundle.handle || 'bundle';
  const sectionId = root.getAttribute('data-section-id') || '';
  // The media column is a sibling of the picker root, so reach up to the section.
  const scope = root.closest('.bundle') || document;
  const forEl = root.querySelector('[data-bundle-for]');
  const savingEl = root.querySelector('[data-bundle-saving]');
  const addBtn = root.querySelector('[data-bundle-add]');
  const buyBox = root.querySelector('.amobler-buy-box');
  const oosNote = root.querySelector('[data-bundle-oos-note]');
  const pickupRow = root.querySelector('[data-bundle-pickup]');
  const addLabelEl = addBtn ? (addBtn.querySelector('span') || addBtn) : null;
  const addLabel = addLabelEl ? addLabelEl.textContent : '';

  const selection = defaultSelection(bundle);
  // Selection keeps the first available option, but the Liquid marks the same
  // option selected, so the first paint matches.

  function money(ore) { return formatMoneyWith(ore, bundle.moneyFormat); }

  function sets() {
    const input = root.querySelector('#product-form-' + CSS.escape(sectionId) + ' quantity-selector input, quantity-selector input');
    const n = input ? parseInt(input.value, 10) : 1;
    return Number.isNaN(n) || n < 1 ? 1 : n;
  }

  function paintButtons() {
    root.querySelectorAll('[data-bundle-option]').forEach((btn) => {
      const slot = btn.getAttribute('data-slot');
      const variantId = Number(btn.getAttribute('data-variant'));
      const chosen = selection[slot] === variantId;
      btn.setAttribute('aria-pressed', chosen ? 'true' : 'false');
      btn.classList.toggle('amobler-thumb--selected', chosen);
    });
    root.querySelectorAll('[data-bundle-drawer-select]').forEach((item) => {
      const chosen = selection[item.getAttribute('data-slot')] === Number(item.getAttribute('data-variant'));
      item.classList.toggle('is-selected', chosen);
      item.setAttribute('aria-pressed', chosen ? 'true' : 'false');
    });
    root.querySelectorAll('[data-bundle-slot]').forEach(keepSelectedVisible);
    (bundle.slots || []).forEach((slot) => {
      const opt = optionFor(slot, selection[slot.key]);
      const valueEl = root.querySelector('[data-bundle-slot-value="' + CSS.escape(slot.key) + '"]');
      if (valueEl && opt) valueEl.textContent = opt.label || opt.title;
    });
  }

  function paintPrice() {
    const forTotal = computeFor(selection, bundle);
    if (forEl) forEl.textContent = money(forTotal);
    if (savingEl) {
      const saving = forTotal - (bundle.naa || 0);
      savingEl.hidden = !(saving > 0);
      if (saving > 0 && bundle.saveLabel) savingEl.textContent = bundle.saveLabel.replace('__AMOUNT__', money(saving));
    }
  }

  // Gallery (theme#15): same swap as a variant change on the product page.
  // The media list for the selection goes into the gallery's map under its
  // key, then the variant:change event amobler-gallery.js listens for fires.
  let mediaData = null;
  const mediaEl = scope.querySelector('[data-bundle-media]');
  if (mediaEl) {
    try { mediaData = JSON.parse(mediaEl.textContent); } catch (e) { mediaData = null; }
  }
  let lastGalleryKey = null;

  function paintGallery() {
    if (!mediaData) return;
    const ids = selectionMedia(selection, bundle, mediaData);
    const key = comboSkuKey(selection, bundle) ||
      (bundle.slots || []).map((s) => selection[s.key]).join('_');
    if (key === lastGalleryKey) return;
    const first = lastGalleryKey === null;
    lastGalleryKey = key;
    if (first || ids.length === 0) return; // Liquid rendered the default selection.
    const mapEl = scope.querySelector('script[data-amobler-variant-media-map]');
    if (!mapEl) return;
    let map = {};
    try { map = JSON.parse(mapEl.textContent) || {}; } catch (e) { map = {}; }
    map[key] = ids;
    mapEl.textContent = JSON.stringify(map);
    document.dispatchEvent(new CustomEvent('variant:change:' + sectionId + ':' + mediaData.galleryId, {
      detail: { variant: { id: key } },
    }));
  }

  // The status line of each selected part, as [{ holder, el, slot }].
  function selectedStatuses() {
    return (bundle.slots || []).map((slot) => {
      const holder = root.querySelector('[data-bundle-status][data-slot="' + CSS.escape(slot.key) +
        '"][data-variant="' + selection[slot.key] + '"]');
      return holder ? { holder, slot, el: holder.querySelector('amobler-delivery-status') } : null;
    }).filter(Boolean);
  }

  // Shows the worst status of the selected parts and blocks the button the
  // way the product page does (presentation only, see theme#24).
  function paintStatus() {
    const statuses = selectedStatuses();
    root.querySelectorAll('[data-bundle-status]').forEach((h) => { h.hidden = true; });
    let worst = null;
    let worstScore = -1;
    statuses.forEach((s) => {
      const main = s.el && s.el.querySelector('[data-delivery-main]');
      const score = statusSeverity(s.el && s.el.state, main ? main.textContent : '');
      if (score > worstScore) { worst = s; worstScore = score; }
    });
    if (worst) worst.holder.hidden = false;

    const state = (worst && worst.el && worst.el.state) || {};
    const blocked = !allAvailable(selection, bundle) || !!state.blocked;
    const contactSales = !!state.hidden;
    if (buyBox) buyBox.classList.toggle('amobler-buy-box--contact-sales', contactSales);
    if (addBtn) addBtn.disabled = blocked;
    if (addLabelEl) addLabelEl.textContent = blocked ? 'Utsolgt' : addLabel;
    if (oosNote) oosNote.hidden = !blocked || contactSales;
    paintPickup(statuses, contactSales);
  }

  // Click and collect: only when one location holds every selected part
  // (decided 13.10). Uses the stock per location the status lines fetched.
  function paintPickup(statuses, contactSales) {
    if (!pickupRow) return;
    const first = statuses[0] && statuses[0].el;
    const parts = statuses.map((s) => ({ node: s.el && s.el.lastNode, units: s.slot.quantity || 1 }));
    const ready = first && parts.every((p) => p.node);
    let common = [];
    let candidates = [];
    if (ready) {
      candidates = [first.dataset.centralLocation]
        .concat(String(first.dataset.pickupLocations || '').split(','))
        .map((n) => (n || '').trim()).filter(Boolean);
      common = commonPickupLocations(parts, candidates, sets());
    }
    pickupRow.hidden = contactSales || common.length === 0;

    document.querySelectorAll('[data-pickup-location][data-pickup-section="' + CSS.escape(bundle.pickupSection || '') + '"]')
      .forEach((row) => {
        if (!ready) return;
        const inStock = common.indexOf(row.getAttribute('data-pickup-location')) !== -1;
        row.setAttribute('data-pickup-state', inStock ? 'in-stock' : 'out-of-stock');
        row.querySelectorAll('[data-pickup-icon]').forEach((icon) => {
          icon.hidden = icon.getAttribute('data-pickup-icon') !== (inStock ? 'in-stock' : 'out-of-stock');
        });
        const text = row.querySelector('[data-pickup-text]');
        if (text) text.textContent = inStock ? 'Kan hentes her' : 'Ikke på lager her';
      });
  }

  // Size and shipping tables of the selected parts (snippets/block.bundle-details).
  function paintDetails() {
    scope.querySelectorAll('[data-bundle-detail]').forEach((el) => {
      el.hidden = selection[el.getAttribute('data-slot')] !== Number(el.getAttribute('data-variant'));
    });
  }

  function refresh() {
    paintPrice();
    paintDetails();
    paintButtons();
    paintGallery();
    paintStatus();
    root.dispatchEvent(new CustomEvent('bundle:change', {
      bubbles: true,
      detail: { selection: Object.assign({}, selection), bundle },
    }));
  }

  function choose(slotKey, variantId) {
    if (selection[slotKey] === variantId) return;
    selection[slotKey] = variantId;
    refresh();
  }

  // A strip with more options than tiles shows the first ones and a "+X" tile;
  // the selected option always stays inline, in the last image slot when it
  // would otherwise be hidden (as amobler.thumbnail-strip on the product page).
  function keepSelectedVisible(strip) {
    const more = strip.querySelector('.amobler-thumb--more');
    if (!more) return;
    const tiles = Array.from(strip.querySelectorAll('[data-bundle-option]'));
    const slots = tiles.length - Number((more.textContent.match(/\+(\d+)/) || [])[1] || 0);
    const selectedIndex = tiles.findIndex((t) => t.classList.contains('amobler-thumb--selected'));
    const keep = selectedIndex >= slots ? slots - 1 : slots;
    tiles.forEach((tile, i) => {
      tile.hidden = !(i < keep || i === selectedIndex);
    });
  }

  // Sold-out options stay clickable, as on the product page: the selection
  // lands on the sold-out part and the buy button shows Utsolgt.
  root.querySelectorAll('[data-bundle-option]').forEach((btn) => {
    btn.addEventListener('click', () => {
      choose(btn.getAttribute('data-slot'), Number(btn.getAttribute('data-variant')));
    });
  });

  root.querySelectorAll('[data-bundle-drawer-trigger]').forEach((trigger) => {
    trigger.addEventListener('click', () => {
      const dialog = root.querySelector('[data-bundle-drawer="' + CSS.escape(trigger.getAttribute('data-bundle-drawer-trigger')) + '"]');
      if (dialog && typeof dialog.showModal === 'function') dialog.showModal();
    });
  });

  root.querySelectorAll('[data-bundle-drawer-select]').forEach((item) => {
    item.addEventListener('click', () => {
      choose(item.getAttribute('data-slot'), Number(item.getAttribute('data-variant')));
      const dialog = item.closest('dialog');
      if (dialog && typeof dialog.close === 'function') dialog.close();
    });
  });

  // Tooltip placement and backdrop close: the product page's own binders.
  import('amobler-pdp')
    .then((pdp) => { pdp.bindDrawerNotes(root); pdp.bindDrawerBackdropClose(root); })
    .catch(() => {});

  // A status line resolved (hydration, or a new quantity): repaint the worst.
  root.addEventListener('amobler:delivery:state', () => paintStatus());

  function openDrawer() {
    // Mirror sofa-configurator.js: refresh the cart, then open the drawer.
    document.dispatchEvent(new CustomEvent('cart:build'));
    document.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));
    const toggle = document.querySelector('cart-drawer, [data-cart-toggle], .js-cart-drawer-toggle');
    if (toggle) {
      if (typeof toggle.open === 'function') toggle.open();
      else if (typeof toggle.click === 'function') toggle.click();
    }
  }

  function addToCart() {
    if (!allAvailable(selection, bundle)) return;
    const bundleId = prefix + '-b' + Date.now().toString(36);
    // N bundles = N x each slot's quantity. _bundle_unit stays the per-set
    // quantity, which is how module.cart-bundle.js reads the multiplier.
    const n = sets();
    const items = buildAddItems(selection, bundle, bundleId)
      .map((item) => Object.assign({}, item, { quantity: item.quantity * n }));
    if (addBtn) addBtn.disabled = true;
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items, sections: ['cart-ajax'] }),
    })
      .then((r) => { if (!r.ok) throw new Error('add failed'); return r.json(); })
      .then(() => { openDrawer(); if (addBtn) addBtn.disabled = false; })
      .catch(() => { if (addBtn) addBtn.disabled = false; });
  }

  if (addBtn) addBtn.addEventListener('click', addToCart);
  refresh();
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  const boot = () => {
    document.querySelectorAll('[data-amobler-bundle-root]').forEach(initBundlePicker);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
}
